/**
 * Selected Strategy Reference — a compact header, not a numbered workflow
 * step (replaces the old, full "Original Recommendation" card, and is
 * deliberately rendered above/outside `StrategyAdjustmentPage`'s own
 * `Stack` of workflow cards rather than as the first item in it).
 *
 * The AI Insights tab (formerly "AI Strategy Recommendation") already
 * owns every recommendation detail — confidence, OI Score, spot,
 * trend/regime, market thesis, and the generated example legs
 * (`StrategyRecommendationSection.tsx`, `BestMatchDetail`). Duplicating
 * that here just to identify which recommendation this Trade Builder
 * started from would be exactly the kind of split-source-of-truth this
 * refactor exists to remove — so this bar shows only enough to confirm
 * "this is the right one" (name/underlying, confidence, regime) plus a
 * link back to the real thing.
 *
 * Read-only, same as the card it replaces: nothing here is editable, and
 * `original` itself is never re-fetched or re-derived by this component —
 * `StrategyAdjustmentPage` still owns that single fetch, this just renders
 * a slice of it.
 *
 * Divergence indicator (review follow-up): once the trader converts the
 * Trade Builder to a different strategy shape than the one shown here,
 * this name/confidence/regime no longer describes what's being built
 * below — `hasDiverged` swaps in a "Modified" callout + a one-click
 * "Reset to recommended" action instead of leaving the two silently
 * disagreeing. `StrategyAdjustmentPage` computes `hasDiverged` itself
 * (comparing its own live `strategyType` state against this same
 * `mapRecommendationKeyToAdjustableType(original.strategy_key)` this
 * component already derives) rather than this component re-deriving
 * something it can't see.
 */
import { Alert, Button, Card, CardContent, Chip, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import type { OriginalRecommendationRead } from "@/types";
import { fmtNum, STRATEGY_TYPE_LABELS, mapRecommendationKeyToAdjustableType } from "./constants";

export function SelectedStrategyBar({
  original,
  onViewRecommendation,
  hasDiverged = false,
  onResetToRecommended,
  resetBusy = false,
}: {
  original: OriginalRecommendationRead;
  onViewRecommendation?: () => void;
  /** `true` once the Trade Builder's current strategy shape no longer
   * matches this recommendation (e.g. the trader used "Convert
   * Strategy"). */
  hasDiverged?: boolean;
  onResetToRecommended?: () => void;
  resetBusy?: boolean;
}) {
  const adjustableType = mapRecommendationKeyToAdjustableType(original.strategy_key);
  const strategyLabel = adjustableType ? STRATEGY_TYPE_LABELS[adjustableType] : original.strategy_display_name;

  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
        >
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography
              variant="body1"
              fontWeight={700}
              sx={hasDiverged ? { color: "text.secondary" } : undefined}
            >
              {original.symbol_code} · {strategyLabel}
            </Typography>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
            <Stack direction="row" spacing={0.75} alignItems="baseline">
              <Typography variant="caption" color="text.secondary">
                Strategy Confidence
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {fmtNum(original.confidence, 0)}%
              </Typography>
            </Stack>
            <Chip size="small" label={original.market_regime ?? "—"} />
            {hasDiverged && <Chip size="small" color="warning" label="Modified" />}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            {hasDiverged && (
              <Button
                size="small"
                color="warning"
                startIcon={resetBusy ? <CircularProgress size={14} color="inherit" /> : <RestartAltIcon fontSize="small" />}
                onClick={() => onResetToRecommended?.()}
                disabled={resetBusy}
              >
                Reset to recommended
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<AssessmentIcon fontSize="small" />}
              onClick={() => onViewRecommendation?.()}
            >
              View Recommendation
            </Button>
          </Stack>
        </Stack>

        {hasDiverged && (
          <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
            You're building a different strategy than the one recommended above — the legs, risk/reward, and
            Greeks below reflect your edits, not the original {strategyLabel} recommendation.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default SelectedStrategyBar;
